/* PeerNest mentor expertise/languages — one editor, application suggestions, explicit save. */
(() => {
  'use strict';
  const root = document.querySelector('[data-pnauth-profile-page]');
  if (!root) return;
  const section = root.querySelector('[data-pnmyp-mentor-extras]');
  if (!section) return;
  let isReady = false;
  let choices = null;
  const q = selector => section.querySelector(selector);
  const normalize = value => String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const list = value => Array.isArray(value) ? value.map(x => String(x ?? '').trim()).filter(Boolean) : [];
  const tell = (text, error = false) => {
    const el = q('[data-pnmchoice-message]');
    if (!el) return;
    el.textContent = text;
    el.hidden = !text;
    el.dataset.type = error ? 'error' : 'success';
  };
  const getChecks = type => [...(q(`[data-pnmchoice-${type}]`)?.querySelectorAll('input[type=checkbox]') || [])];
  const checked = type => getChecks(type).filter(i => i.checked);
  const otherChecked = type => checked(type).some(i => i.dataset.other === 'true');

  function updateCustom(type) {
    const wrap = q(`[data-pnmchoice-${type}-custom-wrap]`);
    const field = q(`[data-pnmchoice-${type}-custom]`);
    const active = otherChecked(type);
    if (wrap) wrap.hidden = !active;
    if (field) field.required = active;
    // Never erase previously saved custom text merely by toggling.
  }

  function render(type, options, savedIds, suggestions, custom) {
    const container = q(`[data-pnmchoice-${type}]`);
    if (!container) throw new Error(`Missing ${type} choices control.`);
    const saved = new Set((savedIds || []).map(String));
    const isInitial = saved.size === 0;
    const suggested = new Set(list(suggestions).map(normalize));
    const matches = new Set();
    const limit = type === 'expertise' ? 5 : 6;
    container.replaceChildren();

    options.forEach(o => {
      const match = normalize(o.name);
      const slug = normalize(o.slug);
      const appMatch = suggested.has(match) || (slug && suggested.has(slug));
      if (appMatch) { matches.add(match); if (slug) matches.add(slug); }
      const label = document.createElement('label');
      label.className = 'pnmchoice__option';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = o.id;
      input.dataset.other = String(slug === 'other' || match === 'other');
      input.checked = saved.has(String(o.id)) || (isInitial && appMatch);
      input.addEventListener('change', () => {
        if (checked(type).length > limit) {
          input.checked = false;
          tell(`Select at most ${limit} ${type === 'expertise' ? 'expertise areas' : 'languages'}.`, true);
        } else tell('');
        updateCustom(type);
      });
      const span = document.createElement('span');
      span.textContent = o.name;
      label.append(input, span);
      container.append(label);
    });

    const customField = q(`[data-pnmchoice-${type}-custom]`);
    if (customField) customField.value = custom || '';
    // Unmatched text remains in an explicit hint; never silently publish or discard it.
    if (isInitial && suggestions.length) {
      const unmatched = suggestions.filter(s => !matches.has(normalize(s)));
      const note = q(`[data-pnmchoice-${type}-suggestions]`);
      if (note) {
        note.hidden = unmatched.length === 0;
        note.textContent = unmatched.length ? `Application answers not matched to the current list: ${unmatched.join(', ')}. Select the closest options or Other and enter them before saving.` : '';
      }
    }
    updateCustom(type);
    return isInitial && suggestions.length > 0;
  }

  async function initialize() {
    isReady = false;
    const db = window.PeerNestDB;
    if (!db?.request) return;
    const response = await db.request('rpc/pn_get_my_mentor_choices_v1', '', {method:'POST',body:{}});
    if (!response || !Array.isArray(response.expertise_options) || !Array.isArray(response.language_options)) {
      throw new Error('Mentor expertise/languages could not be loaded. Check the SQL migration.');
    }
    choices = response;
    const prefE = render('expertise', response.expertise_options, response.expertise_ids,
      list(response.application_expertise), response.custom_expertise_text);
    const prefL = render('language', response.language_options, response.language_ids,
      list(response.application_languages), response.custom_language_text);
    const part = q('[data-pnmchoice-editor]');
    if (part) part.hidden = false;
    isReady = true;
    if (prefE || prefL) {
      tell('We selected matching answers from your original application. Review them and press Save profile to confirm.');
    }
  }

  function validate() {
    if (!isReady) return false;
    const e = checked('expertise'), l = checked('language');
    if (e.length < 1 || e.length > 5 || l.length < 1 || l.length > 6) {
      tell('Choose 1–5 expertise areas and 1–6 languages.', true);
      section.scrollIntoView({block:'center',behavior:'smooth'});
      return false;
    }
    for (const type of ['expertise','language']) {
      const field = q(`[data-pnmchoice-${type}-custom]`);
      if (otherChecked(type) && (!field?.value.trim() || field.value.trim().length > 160)) {
        tell(`Enter valid Other ${type} details (up to 160 characters).`, true);
        field?.focus(); return false;
      }
    }
    return true;
  }

  async function save() {
    if (!validate()) throw new Error('Review your mentor expertise and languages before saving.');
    const db = window.PeerNestDB;
    const result = await db.request('rpc/pn_set_my_mentor_choices_v1', '', {method:'POST',body:{
      p_expertise_ids:checked('expertise').map(i=>i.value),
      p_language_ids:checked('language').map(i=>i.value),
      p_custom_expertise_text:q('[data-pnmchoice-expertise-custom]')?.value.trim() || '',
      p_custom_language_text:q('[data-pnmchoice-language-custom]')?.value.trim() || ''
    }});
    tell('Expertise and languages saved.');
    return result;
  }

  window.PeerNestMentorChoices = Object.freeze({initialize,validate,save,isReady:()=>isReady});
})();
